import type { LinkProps } from "next/link";

export type Url = LinkProps["href"];
export type UrlObject = Exclude<Url, string>;

export type Query = { [key: string]: string | number | boolean };

export const BRAND_INDEX = "/";

export const COMMENTS_INDEX = "/comments";

/* PRODUCTS */

export const PRODUCTS_INDEX = "/products";

export const PRODUCTS_IMPORT = `${PRODUCTS_INDEX}/import` as const;
export const PRODUCTS_ATTRIBUTES = `${PRODUCTS_INDEX}/attributes` as const;

export function selectedProductLine(productLineId: UUID): Url;
export function selectedProductLine(
  productLineId: UUID | null | undefined,
): Url | undefined;
export function selectedProductLine(
  productLineId: UUID | null | undefined,
): Url | undefined {
  if (!productLineId) return;
  return { pathname: PRODUCTS_INDEX, query: { productLineId } };
}

export function selectedSku(skuId: UUID): Url;
export function selectedSku(skuId: UUID | null | undefined): Url | undefined;
export function selectedSku(skuId: UUID | null | undefined): Url | undefined {
  if (!skuId) return;
  return { pathname: PRODUCTS_INDEX, query: { skuId } };
}

/* CUSTOMERS */

export const CUSTOMERS_INDEX = "/customers";

export const CUSTOMER_PAGE = `${CUSTOMERS_INDEX}/[orderingPlatformId]` as const;

export const CUSTOMER_PRODUCTS_PAGE = `${CUSTOMER_PAGE}/products` as const;

export const CUSTOMER_WAREHOUSES_PAGE = `${CUSTOMER_PAGE}/warehouses` as const;

export const CUSTOMER_DOCUMENTS_PAGE = `${CUSTOMER_PAGE}/documents` as const;

export const CUSTOMER_INVENTORY_HEALTH_PAGE =
  `${CUSTOMER_PAGE}/inventory-health` as const;

export const CUSTOMER_REPLENISHMENT_SCHEDULE_PAGE =
  `${CUSTOMER_PAGE}/replenishment-schedule` as const;

export const CUSTOMER_ACCOUNTS_PAGE = `${CUSTOMER_PAGE}/accounts` as const;

export const CUSTOMER_PURCHASE_ORDERS_PAGE =
  `${CUSTOMER_PAGE}/purchase-orders` as const;

export const CUSTOMER_PROMOTIONS_PAGE = `${CUSTOMER_PAGE}/promotions` as const;

export const CUSTOMER_WAREHOUSE_PAGE =
  `${CUSTOMER_PAGE}/warehouses/[distributionCenterId]` as const;

export const CUSTOMER_WAREHOUSE_PRODUCTS_PAGE =
  `${CUSTOMER_WAREHOUSE_PAGE}/products` as const;

export const CUSTOMER_WAREHOUSE_INVENTORY_HEALTH_PAGE =
  `${CUSTOMER_WAREHOUSE_PAGE}/inventory-health` as const;

export const CUSTOMER_WAREHOUSE_REPLENISHMENT_SCHEDULE_PAGE =
  `${CUSTOMER_WAREHOUSE_PAGE}/replenishment-schedule` as const;

export const CUSTOMER_WAREHOUSE_ACCOUNTS_PAGE =
  `${CUSTOMER_WAREHOUSE_PAGE}/accounts` as const;

export const CUSTOMER_WAREHOUSE_PURCHASE_ORDERS_PAGE =
  `${CUSTOMER_WAREHOUSE_PAGE}/purchase-orders` as const;

export const CUSTOMERS_IMPORT = `${CUSTOMERS_INDEX}/import` as const;

export const CUSTOMER_PRODUCT_PRESENCE = `${CUSTOMERS_INDEX}/presence` as const;

export const CUSTOMER_INVENTORY_HEALTH =
  `${CUSTOMERS_INDEX}/inventory-health` as const;

export const CUSTOMER_REPLENISHMENT_SCHEDULE =
  `${CUSTOMERS_INDEX}/replenishment-schedule` as const;

function makeCustomerPageFn(pathname: string) {
  function generatedFn(orderingPlatformId: UUID): UrlObject;
  function generatedFn(
    orderingPlatformId: UUID | null | undefined,
  ): UrlObject | undefined;
  function generatedFn(
    orderingPlatformId: UUID | null | undefined,
  ): UrlObject | undefined {
    if (!orderingPlatformId) return;
    return { pathname, query: { orderingPlatformId } };
  }
  return generatedFn;
}

export const customerPage = makeCustomerPageFn(CUSTOMER_PAGE);
export const customerProductsPage = makeCustomerPageFn(CUSTOMER_PRODUCTS_PAGE);
export const customerWarehousesPage = makeCustomerPageFn(
  CUSTOMER_WAREHOUSES_PAGE,
);
export const customerDocumentsPage = makeCustomerPageFn(
  CUSTOMER_DOCUMENTS_PAGE,
);
export const customerInventoryHealthPage = makeCustomerPageFn(
  CUSTOMER_INVENTORY_HEALTH_PAGE,
);
export const customerReplenishmentSchedulePage = makeCustomerPageFn(
  CUSTOMER_REPLENISHMENT_SCHEDULE_PAGE,
);
export const customerAccountsPage = makeCustomerPageFn(CUSTOMER_ACCOUNTS_PAGE);
export const customerPurchaseOrdersPage = makeCustomerPageFn(
  CUSTOMER_PURCHASE_ORDERS_PAGE,
);
export const customerPromotionsPage = makeCustomerPageFn(
  CUSTOMER_PROMOTIONS_PAGE,
);

function makeCustomerWarehousePageFn(pathname: string) {
  function generatedFn(
    orderingPlatformId: UUID,
    distributionCenterId: UUID,
  ): UrlObject;
  function generatedFn(
    orderingPlatformId: UUID | null | undefined,
    distributionCenterId: UUID | null | undefined,
  ): UrlObject | undefined;
  function generatedFn(
    orderingPlatformId: UUID | null | undefined,
    distributionCenterId: UUID | null | undefined,
  ): UrlObject | undefined {
    if (!orderingPlatformId || !distributionCenterId) return;
    return { pathname, query: { orderingPlatformId, distributionCenterId } };
  }
  return generatedFn;
}

export const customerWarehousePage = makeCustomerWarehousePageFn(
  CUSTOMER_WAREHOUSE_PAGE,
);
export const customerWarehouseProductsPage = makeCustomerWarehousePageFn(
  CUSTOMER_WAREHOUSE_PRODUCTS_PAGE,
);

export const customerWarehouseInventoryHealthPage = makeCustomerWarehousePageFn(
  CUSTOMER_WAREHOUSE_INVENTORY_HEALTH_PAGE,
);
export const customerWarehouseReplenishmentSchedulePage =
  makeCustomerWarehousePageFn(CUSTOMER_WAREHOUSE_REPLENISHMENT_SCHEDULE_PAGE);
export const customerWarehouseAccountsPage = makeCustomerWarehousePageFn(
  CUSTOMER_WAREHOUSE_ACCOUNTS_PAGE,
);
export const customerWarehousePurchaseOrdersPage = makeCustomerWarehousePageFn(
  CUSTOMER_WAREHOUSE_PURCHASE_ORDERS_PAGE,
);

/* RETAILERS */

export const BRAND_RETAILERS_INDEX = "/retailers";

export const BRAND_RETAILER_PAGE =
  `${BRAND_RETAILERS_INDEX}/[retailerId]` as const;

export const BRAND_RETAILER_STATUS_UPDATES_PAGE =
  `${BRAND_RETAILER_PAGE}/status-updates` as const;
export const BRAND_RETAILER_DOCUMENTS_PAGE =
  `${BRAND_RETAILER_PAGE}/documents` as const;
export const BRAND_RETAILER_FORMS_PAGE =
  `${BRAND_RETAILER_PAGE}/forms` as const;
export const BRAND_RETAILER_PRODUCTS_PAGE =
  `${BRAND_RETAILER_PAGE}/products` as const;
export const BRAND_RETAILER_PROMOTIONS_PAGE =
  `${BRAND_RETAILER_PAGE}/promotions` as const;
export const BRAND_RETAILER_SUBMISSIONS_PAGE =
  `${BRAND_RETAILER_PAGE}/submissions` as const;

function makeBrandRetailerPageFn(pathname: string) {
  function generatedFn(retailerId: UUID): UrlObject;
  function generatedFn(
    retailerId: UUID | null | undefined,
  ): UrlObject | undefined;
  function generatedFn(
    retailerId: UUID | null | undefined,
  ): UrlObject | undefined {
    if (!retailerId) return;
    return { pathname, query: { retailerId } };
  }
  return generatedFn;
}

export const brandRetailerPage = makeBrandRetailerPageFn(BRAND_RETAILER_PAGE);
export const brandRetailerStatusUpdatesPage = makeBrandRetailerPageFn(
  BRAND_RETAILER_STATUS_UPDATES_PAGE,
);
export const brandRetailerDocumentsPage = makeBrandRetailerPageFn(
  BRAND_RETAILER_DOCUMENTS_PAGE,
);
export const brandRetailerFormsPage = makeBrandRetailerPageFn(
  BRAND_RETAILER_FORMS_PAGE,
);
export const brandRetailerProductsPage = makeBrandRetailerPageFn(
  BRAND_RETAILER_PRODUCTS_PAGE,
);
export const brandRetailerPromotionsPage = makeBrandRetailerPageFn(
  BRAND_RETAILER_PROMOTIONS_PAGE,
);

export function brandRetailerSubmissionsPage(retailerId: UUID): UrlObject;
export function brandRetailerSubmissionsPage(
  retailerId: UUID | null | undefined,
): UrlObject | undefined;
export function brandRetailerSubmissionsPage(
  retailerId: UUID | null | undefined,
  selectedId: UUID | null | undefined,
): UrlObject | undefined;
export function brandRetailerSubmissionsPage(
  retailerId: UUID | null | undefined,
  selectedId?: UUID | null | undefined,
): UrlObject | undefined {
  if (!retailerId) return;
  return {
    pathname: BRAND_RETAILER_SUBMISSIONS_PAGE,
    query: selectedId ? { retailerId, selectedId } : { retailerId },
  };
}

/* PROMOTIONS */

export const BRAND_PROMOTIONS_INDEX = "/promotions";

export const BRAND_PROMOTIONS_ALL = `${BRAND_PROMOTIONS_INDEX}/all` as const;

export const BRAND_PROMOTIONS_IMPORT =
  `${BRAND_PROMOTIONS_INDEX}/import` as const;

/* DOCUMENTS */

export const DOCUMENTS_INDEX = "/documents";

/* FORMS */

export const FORMS_INDEX = "/forms";

export const FORM_TEMPLATE_PAGE =
  `${FORMS_INDEX}/templates/[templateId]` as const;

export function formTemplatePage(templateId: UUID): Url;
export function formTemplatePage(templateId: UUID, runId: UUID): Url;
export function formTemplatePage(
  templateId: UUID | null | undefined,
): Url | undefined;
export function formTemplatePage(
  templateId: UUID | null | undefined,
  runId: UUID | null | undefined,
): Url | undefined;
export function formTemplatePage(
  templateId: UUID | null | undefined,
  runId?: UUID | null | undefined,
): Url | undefined {
  if (!templateId) return;
  return {
    pathname: FORM_TEMPLATE_PAGE,
    query: runId ? { templateId, runId } : { templateId },
  };
}

/* EMAIL INBOX */

export const EMAIL_INBOX_INDEX = "/inbox";

export function selectedEmailPage(emailMessageId: UUID): Url;
export function selectedEmailPage(
  emailMessageId: UUID | null | undefined,
): Url | undefined;
export function selectedEmailPage(
  emailMessageId: UUID | null | undefined,
): Url | undefined {
  if (!emailMessageId) return;
  return {
    pathname: EMAIL_INBOX_INDEX,
    query: { selectedEmailId: emailMessageId },
  };
}

/* PRICING CALCULATOR */

export const PRICING_CALCULATOR_INDEX = `${PRODUCTS_INDEX}/pricing` as const;

/* SALES PLANNER */

export const SALES_PLAN_INDEX = "/sales-plan";

export const SALES_PLAN_PAGE =
  `${SALES_PLAN_INDEX}/[[...salesPlanId]]` as const;

export function salesPlanPage(salesPlanId: UUID): Url;
export function salesPlanPage(
  salesPlanId: UUID | null | undefined,
): Url | undefined;
export function salesPlanPage(
  salesPlanId: UUID | null | undefined,
): Url | undefined {
  if (!salesPlanId) return;
  return { pathname: SALES_PLAN_PAGE, query: { salesPlanId } };
}

/* PURCHASE ORDERS */

export const PURCHASE_ORDERS_INDEX = "/purchase-orders";

export const PURCHASE_ORDERS_DASHBOARD = PURCHASE_ORDERS_INDEX;

export const PURCHASE_ORDERS_TABLE = `${PURCHASE_ORDERS_INDEX}/all` as const;

export const PURCHASE_ORDERS_IMPORT =
  `${PURCHASE_ORDERS_INDEX}/import` as const;

/* ANALYTICS */

export const ANALYTICS_INDEX = "/analytics";

export const ANALYTICS_DASHBOARD = ANALYTICS_INDEX;

export const ANALYTICS_DATA_SOURCES = `${ANALYTICS_INDEX}/source` as const;

export function analyticsDataSourcePage(dataSourceTypeName: string) {
  return `${ANALYTICS_DATA_SOURCES}?dataSourceType=${dataSourceTypeName}`;
}

export const ANALYTICS_MAPPING_PRODUCTS =
  `${ANALYTICS_DATA_SOURCES}/mapping/products` as const;

export function analyticsMappingProductsPage(dataSourceTypeName: string) {
  return `${ANALYTICS_MAPPING_PRODUCTS}?dataSourceType=${dataSourceTypeName}`;
}

/* EMAIL CAMPAIGNS */

export const EMAIL_CAMPAIGNS_INDEX = "/campaigns";

export const EMAIL_CAMPAIGN_PAGE =
  `${EMAIL_CAMPAIGNS_INDEX}/[campaignId]` as const;

export const NEW_CAMPAIGN_PAGE = `${EMAIL_CAMPAIGNS_INDEX}/new` as const;

export function emailCampaignPage(campaignId: UUID): Url;
export function emailCampaignPage(
  campaignId: UUID | null | undefined,
): Url | undefined;
export function emailCampaignPage(
  campaignId: UUID | null | undefined,
): Url | undefined {
  if (!campaignId) return;
  return { pathname: EMAIL_CAMPAIGN_PAGE, query: { campaignId } };
}

/* PITCHABLE FOR RETAILERS */

export const PITCHABLE_MESSAGE_ACTION = "/message-action";

export const PITCHABLE_SAMPLE_ACTION =
  `${PITCHABLE_MESSAGE_ACTION}/sample` as const;

export function pitchableSampleAction(
  pitchableCampaignEmailId: UUID,
  pitchableCampaignRetailerContactId?: string | null,
): Url;
export function pitchableSampleAction(
  pitchableCampaignEmailId: UUID | undefined | null,
  pitchableCampaignRetailerContactId?: string | null,
): Url | undefined;
export function pitchableSampleAction(
  pitchableCampaignEmailId: UUID | undefined | null,
  pitchableCampaignRetailerContactId?: string | null,
): Url | undefined {
  if (!pitchableCampaignEmailId) return;
  return {
    pathname: PITCHABLE_SAMPLE_ACTION,
    query: {
      pceid: pitchableCampaignEmailId || undefined,
      pcrcid: pitchableCampaignRetailerContactId || undefined,
    },
  };
}

export function pitchableSellSheet(pitchableCampaignId: UUID): string;
export function pitchableSellSheet(
  pitchableCampaignId: UUID | null | undefined,
): string | undefined;
export function pitchableSellSheet(
  pitchableCampaignId: UUID | null | undefined,
) {
  if (!pitchableCampaignId) return;
  return `/server/pitchable/campaign/${pitchableCampaignId}/documents/sell-sheet`;
}

/* SETTINGS */

export const SETTINGS_BASE = "/settings";

const BRAND_SETTINGS_BASE = `${SETTINGS_BASE}/brand` as const;
export const BRAND_SETTINGS = `${BRAND_SETTINGS_BASE}/details` as const;
export const BRAND_CHILDREN = `${BRAND_SETTINGS_BASE}/children` as const;
export const BRAND_BILLING = `${BRAND_SETTINGS_BASE}/billing` as const;
export const BRAND_USER_SETTINGS = `${BRAND_SETTINGS_BASE}/users` as const;

export const USER_PROFILE = `${SETTINGS_BASE}/user` as const;

/* BROKER */

export const BROKER_INDEX = "/broker";

export const BROKER_PAGE = `${BROKER_INDEX}/[brokerSlug]` as const;

export function brokerHome(brokerSlug: string): Url;
export function brokerHome(
  brokerSlug: string | null | undefined,
): Url | undefined;
export function brokerHome(
  brokerSlug: string | null | undefined,
): Url | undefined {
  if (!brokerSlug) return;
  return { pathname: BROKER_PAGE, query: { brokerSlug } };
}

export const BROKER_DISTRIBUTOR_PRESENCE_PAGE =
  `${BROKER_PAGE}/distributor-presence` as const;

export const BROKER_RETAILER_PRESENCE_PAGE =
  `${BROKER_PAGE}/retailer-presence` as const;

export const BROKER_STATUS_UPDATES_PAGE =
  `${BROKER_PAGE}/status-updates` as const;

export const BROKER_EMAILS_INBOX_PAGE = `${BROKER_PAGE}/emails/inbox` as const;

export function brokerPath(brokerSlug: string): string;
export function brokerPath(
  brokerSlug: string | null | undefined,
): string | undefined;
export function brokerPath(
  brokerSlug: string | null | undefined,
): string | undefined {
  if (!brokerSlug) return;
  return `${BROKER_INDEX}/${brokerSlug}`;
}

export function brokerDistributorPresencePath(brokerSlug: string): string;
export function brokerDistributorPresencePath(
  brokerSlug: string | null | undefined,
): string | undefined;
export function brokerDistributorPresencePath(
  brokerSlug: string | null | undefined,
): string | undefined {
  const base = brokerPath(brokerSlug);
  if (!base) return;
  return `${base}/distributor-presence`;
}

export function brokerRetailerPresencePath(brokerSlug: string): string;
export function brokerRetailerPresencePath(
  brokerSlug: string | null | undefined,
): string | undefined;
export function brokerRetailerPresencePath(
  brokerSlug: string | null | undefined,
): string | undefined {
  const base = brokerPath(brokerSlug);
  if (!base) return;
  return `${base}/retailer-presence`;
}

export function brokerStatusUpdatesPath(brokerSlug: string): string;
export function brokerStatusUpdatesPath(
  brokerSlug: string | null | undefined,
): string | undefined;
export function brokerStatusUpdatesPath(
  brokerSlug: string | null | undefined,
): string | undefined {
  const base = brokerPath(brokerSlug);
  if (!base) return;
  return `${base}/status-updates`;
}

export function brokerEmailInboxPath(brokerSlug: string): string;
export function brokerEmailInboxPath(
  brokerSlug: string | null | undefined,
): string | undefined;
export function brokerEmailInboxPath(
  brokerSlug: string | null | undefined,
): string | undefined {
  const base = brokerPath(brokerSlug);
  if (!base) return;
  return `${base}/emails/inbox`;
}

export function brokerDistributorPresencePage(brokerSlug: string): Url;
export function brokerDistributorPresencePage(
  brokerSlug: string | null | undefined,
): Url | undefined;
export function brokerDistributorPresencePage(
  brokerSlug: string | null | undefined,
): Url | undefined {
  if (!brokerSlug) return;
  return { pathname: BROKER_DISTRIBUTOR_PRESENCE_PAGE, query: { brokerSlug } };
}

export function brokerRetailerPresencePage(brokerSlug: string): Url;
export function brokerRetailerPresencePage(
  brokerSlug: string | null | undefined,
): Url | undefined;
export function brokerRetailerPresencePage(
  brokerSlug: string | null | undefined,
): Url | undefined {
  if (!brokerSlug) return;
  return { pathname: BROKER_RETAILER_PRESENCE_PAGE, query: { brokerSlug } };
}

export function brokerStatusUpdatesPage(brokerSlug: string): Url;
export function brokerStatusUpdatesPage(
  brokerSlug: string | null | undefined,
): Url | undefined;
export function brokerStatusUpdatesPage(
  brokerSlug: string | null | undefined,
): Url | undefined {
  if (!brokerSlug) return;
  return { pathname: BROKER_STATUS_UPDATES_PAGE, query: { brokerSlug } };
}

export function brokerEmailInboxPage(brokerSlug: string): Url;
export function brokerEmailInboxPage(
  brokerSlug: string | null | undefined,
): Url | undefined;
export function brokerEmailInboxPage(
  brokerSlug: string | null | undefined,
): Url | undefined {
  if (!brokerSlug) return;
  return { pathname: BROKER_EMAILS_INBOX_PAGE, query: { brokerSlug } };
}

/* ADMIN */

export const ADMIN_INDEX = "/admin";
export const ADMIN_SLACK = `${ADMIN_INDEX}/slack` as const;
export const ADMIN_PRODUCTS_INDEX = `${ADMIN_INDEX}/products` as const;
export const ADMIN_ATTRIBUTES_INDEX = `${ADMIN_INDEX}/attributes` as const;
export const ADMIN_PRODUCT_ATTRIBUTES =
  `${ADMIN_ATTRIBUTES_INDEX}/product` as const;
export const ADMIN_COMPANY_ATTRIBUTES =
  `${ADMIN_ATTRIBUTES_INDEX}/company` as const;
export const ADMIN_FORMBOT_INDEX = `${ADMIN_INDEX}/formbot` as const;
export const ADMIN_FORMBOT_TEMPLATES =
  `${ADMIN_FORMBOT_INDEX}/templates` as const;
export const ADMIN_FORMBOT_RUNS = `${ADMIN_FORMBOT_INDEX}/runs` as const;
export const ADMIN_FORMBOT_DICTIONARY =
  `${ADMIN_FORMBOT_INDEX}/dictionary` as const;

export const ADMIN_IMPORTS_INDEX = `${ADMIN_INDEX}/imports` as const;
export const ADMIN_IMPORT_TRANSFORMS =
  `${ADMIN_IMPORTS_INDEX}/transforms` as const;

export function adminFormBotTemplatePage(formBotTemplateId: UUID): Url;
export function adminFormBotTemplatePage(
  formBotTemplateId: UUID | null | undefined,
): Url | undefined;
export function adminFormBotTemplatePage(
  formBotTemplateId: UUID | null | undefined,
): Url | undefined {
  if (!formBotTemplateId) return;
  return {
    pathname: ADMIN_FORMBOT_TEMPLATES,
    query: { selectedId: formBotTemplateId },
  };
}

export function adminFormBotRunPage(formBotRunId: UUID): Url;
export function adminFormBotRunPage(
  formBotRunId: UUID | null | undefined,
): Url | undefined;
export function adminFormBotRunPage(
  formBotRunId: UUID | null | undefined,
): Url | undefined {
  if (!formBotRunId) return;
  return {
    pathname: ADMIN_FORMBOT_RUNS,
    query: { selectedId: formBotRunId },
  };
}

export function adminFormBotDictionaryPage(formBotCanonicalFieldId: UUID): Url;
export function adminFormBotDictionaryPage(
  formBotCanonicalFieldId: UUID | null | undefined,
): Url | undefined;
export function adminFormBotDictionaryPage(
  formBotCanonicalFieldId: UUID | null | undefined,
): Url | undefined {
  if (!formBotCanonicalFieldId) return;
  return {
    pathname: ADMIN_FORMBOT_DICTIONARY,
    query: { selectedId: formBotCanonicalFieldId },
  };
}

export function adminXlsxImportTransformPage(
  xlsxImportTransformJobId: UUID,
): Url;
export function adminXlsxImportTransformPage(
  xlsxImportTransformJobId: UUID | null | undefined,
): Url | undefined;
export function adminXlsxImportTransformPage(
  xlsxImportTransformJobId: UUID | null | undefined,
): Url | undefined {
  if (!xlsxImportTransformJobId) return;
  return {
    pathname: ADMIN_IMPORT_TRANSFORMS,
    query: { selectedId: xlsxImportTransformJobId },
  };
}

export function adminProductAttributePage(productAttributeId: UUID): Url;
export function adminProductAttributePage(
  productAttributeId: UUID | null | undefined,
): Url | undefined;
export function adminProductAttributePage(
  productAttributeId: UUID | null | undefined,
): Url | undefined {
  if (!productAttributeId) return;
  return {
    pathname: ADMIN_PRODUCT_ATTRIBUTES,
    query: { selectedId: productAttributeId },
  };
}

export function adminCompanyAttributePage(companyAttributeId: UUID): Url;
export function adminCompanyAttributePage(
  companyAttributeId: UUID | null | undefined,
): Url | undefined;
export function adminCompanyAttributePage(
  companyAttributeId: UUID | null | undefined,
): Url | undefined {
  if (!companyAttributeId) return;
  return {
    pathname: ADMIN_COMPANY_ATTRIBUTES,
    query: { selectedId: companyAttributeId },
  };
}

export const ADMIN_BRANDS_INDEX = `${ADMIN_INDEX}/brands` as const;

export const ADMIN_BRANDS_MANAGE = ADMIN_BRANDS_INDEX;

export function adminBrandPage(brandId: UUID): Url;
export function adminBrandPage(
  brandId: UUID | null | undefined,
): Url | undefined;
export function adminBrandPage(
  brandId: UUID | null | undefined,
): Url | undefined {
  if (!brandId) return;
  return {
    pathname: ADMIN_BRANDS_MANAGE,
    query: { brandId, brand: brandId },
  };
}

export const ADMIN_ORDERING_PLATFORMS_INDEX =
  `${ADMIN_INDEX}/ordering-platforms` as const;

export function adminOrderingPlatformPage(orderingPlatformId: UUID): Url;
export function adminOrderingPlatformPage(
  orderingPlatformId: UUID | null | undefined,
): Url | undefined;
export function adminOrderingPlatformPage(
  orderingPlatformId: UUID | null | undefined,
): Url | undefined {
  if (!orderingPlatformId) return;
  return {
    pathname: ADMIN_ORDERING_PLATFORMS_INDEX,
    query: {
      selectedId: orderingPlatformId,
      orderingPlatform: orderingPlatformId,
    },
  };
}

export const ADMIN_DISTRIBUTION_CENTERS_INDEX =
  `${ADMIN_ORDERING_PLATFORMS_INDEX}/distribution-centers` as const;

export function adminDistributionCenterPage(distributionCenterId: UUID): Url;
export function adminDistributionCenterPage(
  distributionCenterId: UUID | null | undefined,
): Url | undefined;
export function adminDistributionCenterPage(
  distributionCenterId: UUID | null | undefined,
): Url | undefined {
  if (!distributionCenterId) return;
  return {
    pathname: ADMIN_DISTRIBUTION_CENTERS_INDEX,
    query: {
      selectedId: distributionCenterId,
      distributionCenter: distributionCenterId,
    },
  };
}

export const ADMIN_RETAILERS_INDEX = `${ADMIN_INDEX}/retailers` as const;

export function adminRetailerPage(retailerId: UUID): Url;
export function adminRetailerPage(
  retailerId: UUID | null | undefined,
): Url | undefined;
export function adminRetailerPage(
  retailerId: UUID | null | undefined,
): Url | undefined {
  if (!retailerId) return;
  return {
    pathname: ADMIN_RETAILERS_INDEX,
    query: {
      selectedId: retailerId,
      retailer: retailerId,
    },
  };
}

export const ADMIN_RETAILER_LOCATIONS_INDEX =
  `${ADMIN_RETAILERS_INDEX}/locations` as const;

export function adminRetailerLocationPage(retailerLocationId: UUID): Url;
export function adminRetailerLocationPage(
  retailerLocationId: UUID | null | undefined,
): Url | undefined;
export function adminRetailerLocationPage(
  retailerLocationId: UUID | null | undefined,
): Url | undefined {
  if (!retailerLocationId) return;
  return {
    pathname: ADMIN_RETAILER_LOCATIONS_INDEX,
    query: {
      selectedId: retailerLocationId,
      location: retailerLocationId,
    },
  };
}

export const ADMIN_RETAILER_LOCATION_GROUPS_INDEX =
  `${ADMIN_RETAILERS_INDEX}/location-groups` as const;

export function adminRetailerLocationGroupPage(
  retailerLocationGroupId: UUID,
): Url;
export function adminRetailerLocationGroupPage(
  retailerLocationGroupId: UUID | null | undefined,
): Url | undefined;
export function adminRetailerLocationGroupPage(
  retailerLocationGroupId: UUID | null | undefined,
): Url | undefined {
  if (!retailerLocationGroupId) return;
  return {
    pathname: ADMIN_RETAILER_LOCATION_GROUPS_INDEX,
    query: {
      selectedId: retailerLocationGroupId,
      locationGroup: retailerLocationGroupId,
    },
  };
}

export const ADMIN_ANALYTICS_INDEX = `${ADMIN_INDEX}/analytics` as const;

export const ADMIN_ANALYTICS_DATA_SOURCES =
  `${ADMIN_ANALYTICS_INDEX}/source` as const;

export const ADMIN_ANALYTICS_GLOBAL_DATA_SOURCES =
  `${ADMIN_ANALYTICS_INDEX}/source/global` as const;

export function adminAnalyticsGlobalDataSourcePage(dataSourceTypeName: string) {
  return `${ADMIN_ANALYTICS_GLOBAL_DATA_SOURCES}?dataSourceType=${dataSourceTypeName}`;
}

export const ADMIN_ANALYTICS_CONNECTIONS_INDEX =
  `${ADMIN_ANALYTICS_INDEX}/connections` as const;

export const ADMIN_ANALYTICS_CONNECTIONS_MANAGE =
  ADMIN_ANALYTICS_CONNECTIONS_INDEX;

export function adminAnalyticsConnectionPage(
  externalDataConnectionId: UUID,
): Url;
export function adminAnalyticsConnectionPage(
  externalDataConnectionId: UUID | null | undefined,
): Url | undefined;
export function adminAnalyticsConnectionPage(
  externalDataConnectionId: UUID | null | undefined,
): Url | undefined {
  if (!externalDataConnectionId) return;
  return {
    pathname: ADMIN_ANALYTICS_CONNECTIONS_MANAGE,
    query: { selectedId: externalDataConnectionId },
  };
}

export const ADMIN_ANALYTICS_CONNECTIONS_TASK_RUNS =
  `${ADMIN_ANALYTICS_CONNECTIONS_INDEX}/task-runs` as const;

export const ADMIN_ANALYTICS_CONNECTIONS_SCHEDULED_TASKS =
  `${ADMIN_ANALYTICS_CONNECTIONS_INDEX}/scheduled-tasks` as const;

export function adminAnalyticsTaskRunPage(
  externalDataTaskRunId: UUID,
  query?: Query,
): Url;
export function adminAnalyticsTaskRunPage(
  externalDataTaskRunId: UUID | null | undefined,
  query?: Query,
): Url | undefined;
export function adminAnalyticsTaskRunPage(
  externalDataTaskRunId: UUID | null | undefined,
  query: Query = {},
): Url | undefined {
  if (!externalDataTaskRunId) return;
  return {
    pathname: ADMIN_ANALYTICS_CONNECTIONS_TASK_RUNS,
    query: { ...query, selectedId: externalDataTaskRunId },
  };
}

export function adminAnalyticsScheduledTaskPage(
  scheduledExternalDataTaskId: UUID,
  query?: Query,
): Url;
export function adminAnalyticsScheduledTaskPage(
  scheduledExternalDataTaskId: UUID | null | undefined,
  query?: Query,
): Url | undefined;
export function adminAnalyticsScheduledTaskPage(
  scheduledExternalDataTaskId: UUID | null | undefined,
  query: Query = {},
): Url | undefined {
  if (!scheduledExternalDataTaskId) return;
  return {
    pathname: ADMIN_ANALYTICS_CONNECTIONS_SCHEDULED_TASKS,
    query: { ...query, selectedId: scheduledExternalDataTaskId },
  };
}

export const ADMIN_ANALYTICS_CONNECTIONS_TASKS =
  `${ADMIN_ANALYTICS_CONNECTIONS_INDEX}/tasks` as const;

export function adminAnalyticsTaskPage(
  externalDataTaskId: UUID,
  query?: Query,
): Url;
export function adminAnalyticsTaskPage(
  externalDataTaskId: UUID | null | undefined,
  query?: Query,
): Url | undefined {
  if (!externalDataTaskId) return;
  return {
    pathname: ADMIN_ANALYTICS_CONNECTIONS_TASKS,
    query: { selectedId: externalDataTaskId, ...query },
  };
}

export const ADMIN_ANALYTICS_MAPPING =
  `${ADMIN_ANALYTICS_INDEX}/mapping` as const;

export function makeAdminAnalyticsMappingPage(baseUrl: string) {
  return function adminAnalyticsMappingPage(
    dataSourceTypeName: string,
    uniqueName?: string,
  ) {
    const base = `${baseUrl}?dataSourceType=${dataSourceTypeName}`;
    if (!uniqueName) return base;
    return `${base}&uniqueName=${encodeURIComponent(uniqueName)}`;
  };
}

export const ADMIN_ANALYTICS_DC_MAPPING =
  `${ADMIN_ANALYTICS_MAPPING}/dc` as const;

export const adminAnalyticsDcMappingPage = makeAdminAnalyticsMappingPage(
  ADMIN_ANALYTICS_DC_MAPPING,
);

export const ADMIN_ANALYTICS_RETAILER_MAPPING =
  `${ADMIN_ANALYTICS_MAPPING}/retailer` as const;

export const adminAnalyticsRetailerMappingPage = makeAdminAnalyticsMappingPage(
  ADMIN_ANALYTICS_RETAILER_MAPPING,
);

export const ADMIN_ANALYTICS_RETAILER_LOCATION_MAPPING =
  `${ADMIN_ANALYTICS_MAPPING}/retailer-location` as const;

export const adminAnalyticsRetailerLocationMappingPage =
  makeAdminAnalyticsMappingPage(ADMIN_ANALYTICS_RETAILER_LOCATION_MAPPING);

export const ADMIN_ANALYTICS_BRAND_MAPPING =
  `${ADMIN_ANALYTICS_MAPPING}/brand` as const;

export const adminAnalyticsBrandMappingPage = makeAdminAnalyticsMappingPage(
  ADMIN_ANALYTICS_BRAND_MAPPING,
);

export const ADMIN_ANALYTICS_PRODUCT_MAPPING =
  `${ADMIN_ANALYTICS_MAPPING}/product` as const;

export const adminAnalyticsProductMappingPage = makeAdminAnalyticsMappingPage(
  ADMIN_ANALYTICS_PRODUCT_MAPPING,
);

export const ADMIN_ANALYTICS_ELEMENTARY_REPORT =
  `${ADMIN_ANALYTICS_INDEX}/elementary-report` as const;

export const ADMIN_AUDIT = `${ADMIN_INDEX}/audit` as const;

export const ADMIN_CAMPAIGNS_INDEX = `${ADMIN_INDEX}/campaigns` as const;

export const ADMIN_CAMPAIGNS_DASHBOARD =
  `${ADMIN_CAMPAIGNS_INDEX}/dashboard` as const;

export const ADMIN_CAMPAIGNS_INBOX = `${ADMIN_CAMPAIGNS_INDEX}/inbox` as const;

export const ADMIN_EMAILS_INBOX = "/admin/emails/inbox";

export function adminSelectedEmailPage(emailMessageId: UUID): Url;
export function adminSelectedEmailPage(
  emailMessageId: UUID | null | undefined,
): Url | undefined;
export function adminSelectedEmailPage(
  emailMessageId: UUID | null | undefined,
): Url | undefined {
  if (!emailMessageId) return;
  return {
    pathname: ADMIN_EMAILS_INBOX,
    query: { selectedEmailId: emailMessageId },
  };
}

export const ADMIN_CAMPAIGNS_MANAGE_INDEX =
  `${ADMIN_CAMPAIGNS_INDEX}/manage` as const;
export const ADMIN_CAMPAIGN_PAGE =
  `${ADMIN_CAMPAIGNS_MANAGE_INDEX}/[campaignId]` as const;

export function adminCampaignPage(campaignId: UUID): Url;
export function adminCampaignPage(
  campaignId: UUID | null | undefined,
): Url | undefined;
export function adminCampaignPage(
  campaignId: UUID | null | undefined,
): Url | undefined {
  if (!campaignId) return;
  return { pathname: ADMIN_CAMPAIGN_PAGE, query: { campaignId } };
}
